/**
 * Deep equality for JSON-shaped values (primitives, arrays, plain objects). Object key order is
 * irrelevant, which is why it exists instead of comparing `JSON.stringify` output: the server may
 * reorder keys between two refresh responses that carry the same data. Anything that is not
 * JSON-shaped (Date, Map, class instances, functions) compares by reference.
 * Not `lodash/isEqual`: the auth store is in the eagerly loaded bundle, and that import moves
 * about 15 KB of lodash internals (4.5 KB gzip) out of the lazy editor chunk into it.
 */
export function structuralEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

  const aIsArray = Array.isArray(a);
  if (aIsArray !== Array.isArray(b)) return false;
  if (aIsArray) {
    const bArr = b as unknown[];
    return a.length === bArr.length && a.every((item, i) => structuralEqual(item, bArr[i]));
  }

  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => Object.hasOwn(b, key) && structuralEqual(a[key], b[key]));
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
